import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Drawer, Fab, FormControl, InputLabel, MenuItem, Select, Snackbar, TextField, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PageHeader from '../components/layout/PageHeader';
import TaskList from '../components/tasks/TaskList';
import TaskForm from '../components/tasks/TaskForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import usePageTitle from '../hooks/usePageTitle';
import { Task, TaskFormData, getTasks, getTask, createTask, updateTask, deleteTask } from '../services/tasks';
import { Topic, getTopics, updateTopic } from '../services/topics';
import { Planner, getPlanners } from '../services/planners';
import { useAuth } from '../hooks/useAuth';

// ── Mobile filter types & helpers ────────────────────────────
type MobileFilters = {
  statuses: Set<'pending' | 'in_progress' | 'completed'>;
  priorities: Set<'low' | 'medium' | 'high'>;
  planners: Set<string>;
  topics: Set<string>;
  dateRange: 'any' | 'overdue' | 'today' | 'week' | 'month' | 'no_date';
};

function emptyMobileFilters(): MobileFilters {
  return {
    statuses: new Set(),
    priorities: new Set(),
    planners: new Set(),
    topics: new Set(),
    dateRange: 'any',
  };
}

function mobileFilterCount(f: MobileFilters): number {
  return f.statuses.size + f.priorities.size + f.planners.size + f.topics.size +
    (f.dateRange !== 'any' ? 1 : 0);
}

function getMobileDateDiff(iso: string | null | undefined): { overdue: boolean; days: number } | null {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(iso + 'T00:00:00');
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  return { overdue: diff < 0, days: diff };
}

function passesMobileFilters(task: { status: string; priority: string; assigned_planner_id?: string | null; topic_id?: string | null; due_date?: string | null }, f: MobileFilters): boolean {
  if (f.statuses.size && !f.statuses.has(task.status as 'pending' | 'in_progress' | 'completed')) return false;
  if (f.priorities.size && !f.priorities.has(task.priority as 'low' | 'medium' | 'high')) return false;
  if (f.planners.size && !f.planners.has(task.assigned_planner_id ?? '')) return false;
  if (f.topics.size && !f.topics.has(task.topic_id ?? '')) return false;
  if (f.dateRange !== 'any') {
    if (f.dateRange === 'no_date') {
      if (task.due_date) return false;
    } else {
      const d = getMobileDateDiff(task.due_date);
      if (!d) return false;
      if (f.dateRange === 'overdue' && !(d.overdue && task.status !== 'completed')) return false;
      if (f.dateRange === 'today' && d.days !== 0) return false;
      if (f.dateRange === 'week' && !(d.days >= 0 && d.days <= 7)) return false;
      if (f.dateRange === 'month' && !(d.days >= 0 && d.days <= 31)) return false;
    }
  }
  return true;
}

const MOBILE_STATUS_SHEET_CFG: Record<string, { label: string; color: string; bg: string; stripe: string }> = {
  pending:     { label: 'To do',       color: '#7A7269', bg: '#E4DED8',                stripe: '#B5ADA5' },
  in_progress: { label: 'In progress', color: '#4A6E8A', bg: 'rgba(106,142,176,0.15)', stripe: '#6A8EB0' },
  completed:   { label: 'Done',        color: '#4A7352', bg: 'rgba(107,158,120,0.15)', stripe: '#6B9E78' },
};

const PRIORITY_CFG: Record<string, { label: string; color: string; dot: string }> = {
  high:   { label: 'High',   color: '#C26A6A', dot: '#C26A6A' },
  medium: { label: 'Medium', color: '#A68845', dot: '#D4A84E' },
  low:    { label: 'Low',    color: '#7A7269', dot: '#B5ADA5' },
};

const DATE_RANGE_LABELS: Record<string, string> = {
  overdue:  'Overdue',
  today:    'Today',
  week:     'This week',
  month:    'This month',
  no_date:  'No date',
};

// ── MobileFiltersSheet ────────────────────────────────────────
interface MobileFiltersSheetProps {
  filters: MobileFilters;
  setFilters: React.Dispatch<React.SetStateAction<MobileFilters>>;
  planners: { id: string; name: string; color?: string | null }[];
  topics: { id: string; name: string; emoji?: string | null }[];
  mePlannerId: string | null;
  onClose: () => void;
}

const MobileFiltersSheet: React.FC<MobileFiltersSheetProps> = ({ filters, setFilters, planners, topics, mePlannerId, onClose }) => {
  const [local, setLocal] = useState<MobileFilters>(() => ({
    statuses: new Set(filters.statuses),
    priorities: new Set(filters.priorities),
    planners: new Set(filters.planners),
    topics: new Set(filters.topics),
    dateRange: filters.dateRange,
  }));

  const toggleSet = <K extends 'statuses' | 'priorities' | 'planners' | 'topics'>(key: K, value: string) => {
    setLocal(prev => {
      const set = new Set(prev[key] as Set<string>);
      if (set.has(value)) set.delete(value); else set.add(value);
      return { ...prev, [key]: set };
    });
  };

  const activeCount = mobileFilterCount(local);

  const apply = () => { setFilters(local); onClose(); };

  const clear = () => {
    const empty = emptyMobileFilters();
    setLocal(empty);
    setFilters(empty);
  };

  const sectionLabel = (label: string) => (
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase', mb: '9px' }}>
      {label}
    </Typography>
  );

  const pill = (opts: { active: boolean; onClick: () => void; label: React.ReactNode; color?: string | null; dot?: string | null }) => (
    <Box
      component="button"
      onClick={opts.onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        height: 30,
        px: '11px',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: opts.active ? (opts.color || 'primary.main') : 'grey.200',
        bgcolor: opts.active ? (opts.color ? `${opts.color}22` : 'rgba(176,122,143,0.12)') : 'background.paper',
        color: opts.active ? (opts.color || '#8E5C72') : 'grey.800',
        fontSize: 12.5,
        fontWeight: opts.active ? 600 : 500,
        letterSpacing: '-0.1px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        '&:active': { opacity: 0.8 },
      }}
    >
      {opts.dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: opts.dot, flexShrink: 0 }} />}
      {opts.label}
    </Box>
  );

  return (
    <>
      {/* Grabber */}
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
        <Box sx={{ width: 36, height: 4, borderRadius: '2px', bgcolor: 'grey.200' }} />
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '18px', pt: '12px', pb: '14px' }}>
        <Box
          component="button"
          onClick={clear}
          sx={{
            background: 'transparent',
            border: 'none',
            color: activeCount ? 'error.main' : 'grey.400',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            p: 0,
            '&:active': { opacity: 0.7 },
          }}
        >
          Clear
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'grey.900', letterSpacing: '-0.2px' }}>
          Filters
        </Typography>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            background: 'transparent',
            border: 'none',
            color: 'primary.main',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            p: 0,
            '&:active': { opacity: 0.7 },
          }}
        >
          Done
        </Box>
      </Box>

      {/* Scrollable sections */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* Status */}
        <Box sx={{ px: '18px', py: '14px', borderBottom: '1px solid', borderColor: 'grey.100' }}>
          {sectionLabel('Status')}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {([
              { v: 'pending',     label: 'To do',       dot: '#B5ADA5' },
              { v: 'in_progress', label: 'In progress', dot: '#6A8EB0' },
              { v: 'completed',   label: 'Done',        dot: '#6B9E78' },
            ] as const).map(o => pill({ active: local.statuses.has(o.v), onClick: () => toggleSet('statuses', o.v), label: o.label, dot: o.dot }))}
          </Box>
        </Box>

        {/* Priority */}
        <Box sx={{ px: '18px', py: '14px', borderBottom: '1px solid', borderColor: 'grey.100' }}>
          {sectionLabel('Priority')}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {([
              { v: 'high',   label: 'High',   color: '#C26A6A', dot: '#C26A6A' },
              { v: 'medium', label: 'Medium', color: '#A68845', dot: '#D4A84E' },
              { v: 'low',    label: 'Low',    color: '#7A7269', dot: '#B5ADA5' },
            ] as const).map(o => pill({ active: local.priorities.has(o.v), onClick: () => toggleSet('priorities', o.v), label: o.label, color: o.color, dot: o.dot }))}
          </Box>
        </Box>

        {/* Due date */}
        <Box sx={{ px: '18px', py: '14px', borderBottom: '1px solid', borderColor: 'grey.100' }}>
          {sectionLabel('Due date')}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {([
              { v: 'any',     label: 'Any time' },
              { v: 'overdue', label: 'Overdue' },
              { v: 'today',   label: 'Today' },
              { v: 'week',    label: 'This week' },
              { v: 'month',   label: 'This month' },
              { v: 'no_date', label: 'No date' },
            ] as const).map(o => pill({ active: local.dateRange === o.v, onClick: () => setLocal(prev => ({ ...prev, dateRange: o.v })), label: o.label }))}
          </Box>
        </Box>

        {/* Planner */}
        {planners.length > 0 && (
          <Box sx={{ px: '18px', py: '14px', borderBottom: '1px solid', borderColor: 'grey.100' }}>
            {sectionLabel('Planner')}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {planners.map(p => pill({
                active: local.planners.has(p.id),
                onClick: () => toggleSet('planners', p.id),
                label: p.id === mePlannerId ? (<>Me <span style={{ fontSize: '0.9em' }}>⭐</span></>) : p.name,
                color: p.color,
                dot: p.color,
              }))}
            </Box>
          </Box>
        )}

        {/* Topic */}
        {topics.length > 0 && (
          <Box sx={{ px: '18px', py: '14px', borderBottom: '1px solid', borderColor: 'grey.100' }}>
            {sectionLabel('Topic')}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {topics.map(t => pill({
                active: local.topics.has(t.id),
                onClick: () => toggleSet('topics', t.id),
                label: <>{t.emoji && <span style={{ fontSize: 13, marginRight: 2 }}>{t.emoji}</span>}{t.name}</>,
              }))}
            </Box>
          </Box>
        )}
        <Box sx={{ height: 14 }} />
      </Box>

      {/* Apply footer */}
      <Box sx={{ px: 2, pt: '10px', pb: '22px', borderTop: '1px solid', borderColor: 'grey.100', bgcolor: 'background.paper' }}>
        <Box
          component="button"
          onClick={apply}
          sx={{
            display: 'block',
            width: '100%',
            height: 46,
            borderRadius: '12px',
            border: 'none',
            bgcolor: 'primary.main',
            color: '#fff',
            fontSize: 14.5,
            fontWeight: 600,
            cursor: 'pointer',
            letterSpacing: '-0.1px',
            '&:active': { opacity: 0.85 },
          }}
        >
          {activeCount ? `Apply ${activeCount} filter${activeCount !== 1 ? 's' : ''}` : 'Show all tasks'}
        </Box>
      </Box>
    </>
  );
};

// ─────────────────────────────────────────────────────────────

const TasksPage: React.FC = () => {
  usePageTitle('Tasks');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [planners, setPlanners] = useState<Planner[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);
  const [showFab, setShowFab] = useState(false);
  const [isDefaultSort, setIsDefaultSort] = useState(true);
  const resetSortRef = useRef<(() => void) | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowFab(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [completionSnack, setCompletionSnack] = useState('');
  const [topicCoverPrompt, setTopicCoverPrompt] = useState<{ topic_id: string; topic_name: string } | null>(null);

  // Mobile filters
  const [mobileFilters, setMobileFilters] = useState<MobileFilters>(emptyMobileFilters);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const { user } = useAuth();
  const mePlannerId = React.useMemo(() => {
    if (!user?.email) return null;
    const email = user.email.toLowerCase();
    return planners.find(p => p.email && p.email.toLowerCase() === email)?.id ?? null;
  }, [user, planners]);
  const meActive = !!mePlannerId && mobileFilters.planners.has(mePlannerId);

  const loadTasks = useCallback(async () => {
    try {
      const res = await getTasks();
      setAllTasks(res.data);
      return res.data;
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      const [, pRes, tRes] = await Promise.all([loadTasks(), getPlanners(), getTopics()]);
      setPlanners(pRes.data);
      setTopics(tRes.data);
    };
    load();
  }, [loadTasks]);

  // Handle ?view=taskId from dashboard navigation — open edit form
  const viewHandled = useRef(false);
  useEffect(() => {
    const viewId = searchParams.get('view');
    if (viewId && !loading && !viewHandled.current) {
      viewHandled.current = true;
      getTask(viewId).then((res) => {
        setEditingTask(res.data);
        setFormOpen(true);
        setSearchParams({}, { replace: true });
      }).catch(console.error);
    }
  }, [searchParams, loading, setSearchParams]);

  // Flat list of all tasks, apply filters
  const searchLower = search.toLowerCase();
  const filteredTasks = allTasks.filter((t) => {
    if (search && !t.title.toLowerCase().includes(searchLower)) return false;
    if (statusFilter && t.status !== statusFilter) return false;
    if (priorityFilter && t.priority !== priorityFilter) return false;
    if (assigneeFilter && t.assigned_planner_id !== assigneeFilter) return false;
    if (topicFilter && t.topic_id !== topicFilter) return false;
    if (isMobile && !passesMobileFilters(t, mobileFilters)) return false;
    return true;
  });

  const handleView = (task: Task) => {
    setEditingTask(task);
    setFormOpen(true);
  };

  const handleSave = async (data: TaskFormData) => {
    try {
      if (editingTask) {
        const res = await updateTask(editingTask.id, data);
        if (res.data.topic_all_tasks_completed) {
          setTopicCoverPrompt(res.data.topic_all_tasks_completed);
        }
      } else {
        await createTask(data);
      }
      setFormOpen(false);
      setEditingTask(null);
      loadTasks();
      getTopics().then((res) => setTopics(res.data));
    } catch (err) {
      console.error(err);
    }
  };

  const handleStatusChange = async (task: Task, status: string) => {
    try {
      // Optimistic update — instantly reflect in UI
      const typedStatus = status as Task['status'];
      setAllTasks(prev => prev.map(t => {
        if (t.id === task.id) return { ...t, status: typedStatus };
        // If completing a parent, also optimistically complete its subtasks
        if (status === 'completed' && t.parent_task_id === task.id) return { ...t, status: 'completed' as const };
        return t;
      }));

      const res = await updateTask(task.id, { status });
      const freshTasks = await loadTasks();
      getTopics().then((r) => setTopics(r.data));
      if (res.data.topic_all_tasks_completed) {
        setTopicCoverPrompt(res.data.topic_all_tasks_completed);
      } else if (status === 'completed' && task.status !== 'completed' && freshTasks) {
        const remaining = freshTasks.filter((t) => t.status !== 'completed').length;
        setCompletionSnack(`${remaining} task${remaining !== 1 ? 's' : ''} remaining`);
      }
    } catch (err) {
      console.error(err);
      loadTasks(); // Revert on error by reloading
    }
  };

  const handleCoverTopic = async () => {
    if (!topicCoverPrompt) return;
    try {
      await updateTopic(topicCoverPrompt.topic_id, { status: 'covered' });
      setTopicCoverPrompt(null);
      getTopics().then((res) => setTopics(res.data));
    } catch (err) {
      console.error(err);
    }
  };

  const handlePriorityChange = async (task: Task, priority: string) => {
    try {
      await updateTask(task.id, { priority });
      loadTasks();
    } catch (err) {
      console.error(err);
    }
  };

  const handlePinToggle = async (task: Task) => {
    try {
      await updateTask(task.id, { is_pinned: !task.is_pinned });
      loadTasks();
    } catch (err) {
      console.error(err);
    }
  };

  const handleAssigneeChange = async (task: Task, plannerId: string | null) => {
    try {
      await updateTask(task.id, { assigned_planner_id: plannerId });
      loadTasks();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTask(deleteTarget.id);
      setDeleteTarget(null);
      loadTasks();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <div ref={headerRef}>
        <PageHeader
          title="Tasks"
          {...(isMobile
            ? {
                actions: (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {/* Me filter quick-toggle */}
                    {mePlannerId && (
                      <Box
                        component="button"
                        onClick={() => {
                          setMobileFilters(f => {
                            const next = new Set(f.planners);
                            if (mePlannerId && next.has(mePlannerId)) next.delete(mePlannerId);
                            else if (mePlannerId) next.add(mePlannerId);
                            return { ...f, planners: next };
                          });
                        }}
                        aria-label="Toggle my tasks"
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '10px',
                          border: '1px solid',
                          borderColor: meActive ? '#8E5C72' : 'grey.200',
                          bgcolor: meActive ? 'rgba(176,122,143,0.15)' : 'background.paper',
                          color: meActive ? '#8E5C72' : 'text.primary',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          p: 0,
                          flexShrink: 0,
                          '&:active': { opacity: 0.8 },
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.6"/>
                          <path d="M3 13.5c0-2.485 2.239-4.5 5-4.5s5 2.015 5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                        </svg>
                      </Box>
                    )}
                    {/* Filters button */}
                    <Box
                      component="button"
                      onClick={() => setMobileFiltersOpen(true)}
                      sx={{
                        position: 'relative',
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        p: 0,
                        flexShrink: 0,
                        '&:active': { opacity: 0.8 },
                      }}
                      aria-label="Open filters"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M2 4h12M4 8h8M6 12h4" stroke="#3D3731" strokeWidth="1.6" strokeLinecap="round"/>
                      </svg>
                      {mobileFilterCount(mobileFilters) > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            minWidth: 16,
                            height: 16,
                            px: '4px',
                            borderRadius: '8px',
                            bgcolor: 'error.main',
                            color: '#fff',
                            fontSize: 10,
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px solid',
                            borderColor: 'background.default',
                            boxSizing: 'content-box',
                            lineHeight: 1,
                          }}
                        >
                          {mobileFilterCount(mobileFilters)}
                        </Box>
                      )}
                    </Box>
                    {/* New Task (icon-only on mobile) */}
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={() => { setEditingTask(null); setFormOpen(true); }}
                      aria-label="New Task"
                      sx={{
                        minWidth: 0,
                        width: 40,
                        height: 40,
                        p: 0,
                        borderRadius: '10px',
                        boxShadow: 'none',
                        '&:hover': { boxShadow: 'none', transform: 'none' },
                        '&:active': { transform: 'none' },
                      }}
                    >
                      <AddIcon />
                    </Button>
                  </Box>
                ),
              }
            : { actionLabel: 'New Task', onAction: () => { setEditingTask(null); setFormOpen(true); } })}
        />
      </div>
      <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search tasks..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200, flexGrow: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Priority</InputLabel>
          <Select value={priorityFilter} label="Priority" onChange={(e) => setPriorityFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Assignee</InputLabel>
          <Select value={assigneeFilter} label="Assignee" onChange={(e) => setAssigneeFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {planners.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.id === mePlannerId ? (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    Me <span style={{ fontSize: '0.9em' }}>⭐</span>
                  </Box>
                ) : (
                  p.name
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Topic</InputLabel>
          <Select value={topicFilter} label="Topic" onChange={(e) => setTopicFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {topics.map((t) => <MenuItem key={t.id} value={t.id}>{t.emoji && `${t.emoji} `}{t.name}</MenuItem>)}
          </Select>
        </FormControl>
        {!isDefaultSort && (
          <Button
            size="small"
            startIcon={<RestartAltIcon />}
            onClick={() => resetSortRef.current?.()}
            sx={{ color: 'text.secondary', ml: 'auto' }}
          >
            Default Sort
          </Button>
        )}
      </Box>

      {/* Mobile active-filter chip row */}
      {isMobile && mobileFilterCount(mobileFilters) > 0 && (() => {
        const chips: { key: string; label: string; color?: string; onRemove: () => void }[] = [];
        mobileFilters.statuses.forEach((v) => {
          const cfg = MOBILE_STATUS_SHEET_CFG[v];
          chips.push({ key: `s-${v}`, label: cfg?.label ?? v, onRemove: () => setMobileFilters(f => { const s = new Set(f.statuses); s.delete(v); return { ...f, statuses: s }; }) });
        });
        mobileFilters.priorities.forEach((v) => {
          const cfg = PRIORITY_CFG[v];
          chips.push({ key: `p-${v}`, label: cfg?.label ?? v, color: cfg?.color, onRemove: () => setMobileFilters(f => { const s = new Set(f.priorities); s.delete(v); return { ...f, priorities: s }; }) });
        });
        mobileFilters.planners.forEach((v) => {
          const p = planners.find(x => x.id === v);
          const label = v === mePlannerId ? 'Me ⭐' : (p?.name ?? '?');
          chips.push({ key: `pl-${v}`, label, color: p?.color ?? undefined, onRemove: () => setMobileFilters(f => { const s = new Set(f.planners); s.delete(v); return { ...f, planners: s }; }) });
        });
        mobileFilters.topics.forEach((v) => {
          const t = topics.find(x => x.id === v);
          chips.push({ key: `t-${v}`, label: `${t?.emoji ?? ''} ${t?.name ?? ''}`.trim(), onRemove: () => setMobileFilters(f => { const s = new Set(f.topics); s.delete(v); return { ...f, topics: s }; }) });
        });
        if (mobileFilters.dateRange !== 'any') {
          chips.push({ key: 'd', label: DATE_RANGE_LABELS[mobileFilters.dateRange] ?? mobileFilters.dateRange, onRemove: () => setMobileFilters(f => ({ ...f, dateRange: 'any' })) });
        }
        return (
          <Box sx={{ display: 'flex', gap: '6px', pb: '10px', overflowX: 'auto', alignItems: 'center' }}>
            <span style={{ fontSize: 10.5, color: '#7A7269', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', flexShrink: 0 }}>
              Filters:
            </span>
            {chips.map((c) => (
              <Box
                key={c.key}
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  height: 22,
                  pl: '8px',
                  pr: '4px',
                  borderRadius: '6px',
                  bgcolor: c.color ? `${c.color}22` : 'rgba(176,122,143,0.12)',
                  color: c.color || '#8E5C72',
                  fontWeight: 600,
                  fontSize: 11,
                  letterSpacing: '-0.1px',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {c.label}
                <Box
                  component="button"
                  onClick={c.onRemove}
                  sx={{
                    width: 15,
                    height: 15,
                    borderRadius: '50%',
                    border: 'none',
                    bgcolor: 'rgba(0,0,0,0.08)',
                    color: 'inherit',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0,
                    fontSize: 11,
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >
                  ×
                </Box>
              </Box>
            ))}
          </Box>
        );
      })()}

      {/* Mobile Filters bottom sheet */}
      <Drawer
        anchor="bottom"
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '86%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        <MobileFiltersSheet
          filters={mobileFilters}
          setFilters={setMobileFilters}
          planners={planners}
          topics={topics}
          mePlannerId={mePlannerId}
          onClose={() => setMobileFiltersOpen(false)}
        />
      </Drawer>
      {loading ? <LoadingSpinner /> : (
        <TaskList
          tasks={filteredTasks}
          planners={planners}
          topics={topics}
          loading={false}
          onView={handleView}
          onEdit={(t) => { setEditingTask(t); setFormOpen(true); }}
          onDelete={setDeleteTarget}
          onStatusChange={handleStatusChange}
          onPriorityChange={handlePriorityChange}
          onAssigneeChange={handleAssigneeChange}
          onPinToggle={handlePinToggle}
          onDefaultSortChange={setIsDefaultSort}
          onResetSortRef={resetSortRef}
        />
      )}
      <TaskForm
        open={formOpen}
        task={editingTask}
        planners={planners}
        topics={topics}
        onSave={handleSave}
        onClose={() => { setFormOpen(false); setEditingTask(null); }}
        onDelete={(t) => { setFormOpen(false); setEditingTask(null); setDeleteTarget(t); }}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Task"
        message={`Are you sure you want to delete "${deleteTarget?.title}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
      <ConfirmDialog
        open={!!topicCoverPrompt}
        title="All Tasks Completed"
        message={`All tasks under "${topicCoverPrompt?.topic_name}" are completed. Mark this topic as covered?`}
        confirmLabel="Mark Covered"
        confirmColor="success"
        onConfirm={handleCoverTopic}
        onCancel={() => setTopicCoverPrompt(null)}
      />
      <Snackbar
        open={!!completionSnack}
        autoHideDuration={3000}
        onClose={() => setCompletionSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCompletionSnack('')} variant="filled">
          {completionSnack}
        </Alert>
      </Snackbar>
      {showFab && (
        <Tooltip title="New Task">
          <Fab
            color="primary"
            onClick={() => { setEditingTask(null); setFormOpen(true); }}
            sx={{ position: 'fixed', bottom: 24, right: 24 }}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
      )}
    </>
  );
};

export default TasksPage;
